import { setSession, setSessionEmployee } from "./session";
import { FIREBASE_LOGIN } from "../graphql/mutations/Auth";
import { EMPLOYEE_LOGIN } from "../graphql/mutations/Auth";
import client from "../graphql/client";
import toast from "react-hot-toast";

/**
 * Handle authentication from URL parameters when redirected from wrong domain
 * This should be called on app initialization
 */
export const handleUrlAuthentication = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const loginType = urlParams.get("loginType");

  if (!loginType) {
    return false; // No URL authentication needed
  }

  console.log(`🔄 Handling URL authentication for ${loginType} login`);

  try {
    if (loginType === "phone") {
      return await handlePhoneLoginFromUrl(urlParams);
    } else if (loginType === "employee") {
      return await handleEmployeeLoginFromUrl(urlParams);
    }
  } catch (error) {
    console.error("Error handling URL authentication:", error);
    toast.error("Authentication failed. Please try logging in again.");
    clearUrlParams();
    return false;
  }

  return false;
};

/**
 * Handle phone login re-authentication from URL parameters
 */
const handlePhoneLoginFromUrl = async (urlParams) => {
  const firebaseToken = urlParams.get("firebaseToken");
  // const phoneNumber = urlParams.get('phoneNumber'); // Available if needed for additional validation

  if (!firebaseToken) {
    console.error("Missing Firebase token in URL parameters");
    toast.error("Missing authentication token. Please try logging in again.");
    clearUrlParams();
    return false;
  }

  console.log("🔄 Re-authenticating phone login with Firebase token");

  try {
    // Call your backend's firebaseLogin mutation
    const { data: firebaseLoginResponse } = await client.mutate({
      mutation: FIREBASE_LOGIN,
      variables: {
        idToken: firebaseToken,
      },
    });

    const firebaseLoginData = firebaseLoginResponse.firebaseLogin;

    if (firebaseLoginData?.token) {
      // Set session with the response
      setSession(firebaseLoginData);

      console.log("✅ Phone login re-authentication successful");
      toast.success("Successfully authenticated! Welcome back.");

      // Clear URL parameters
      clearUrlParams();

      // Navigate to appropriate page based on business profiles
      // You might want to check for business profiles here like in your original login
      return true;
    } else {
      console.error("Firebase login failed:", firebaseLoginData?.message);
      toast.error("Authentication failed. Please try logging in again.");
      clearUrlParams();
      return false;
    }
  } catch (error) {
    console.error("Phone login re-authentication error:", error);
    toast.error("Authentication failed. Please try logging in again.");
    clearUrlParams();
    return false;
  }
};

/**
 * Handle employee login re-authentication from URL parameters
 */
const handleEmployeeLoginFromUrl = async (urlParams) => {
  const encodedCredentials = urlParams.get("credentials");

  if (!encodedCredentials) {
    console.error("Missing employee credentials in URL parameters");
    toast.error("Missing authentication credentials. Please try logging in again.");
    clearUrlParams();
    return false;
  }

  console.log("🔄 Re-authenticating employee login with encoded credentials");

  try {
    // Decode the credentials
    const credentials = JSON.parse(atob(encodedCredentials));
    const { employeeId, password } = credentials;

    if (!employeeId || !password) {
      console.error("Invalid credentials in URL parameters");
      toast.error("Invalid authentication credentials. Please try logging in again.");
      clearUrlParams();
      return false;
    }

    // Call your backend's employeeLogin mutation
    const { data: employeeLoginResponse } = await client.mutate({
      mutation: EMPLOYEE_LOGIN,
      variables: {
        employeeId,
        password,
      },
    });

    const employeeLoginData = employeeLoginResponse.employeeLogin;

    if (employeeLoginData?.token) {
      // Set employee session
      setSessionEmployee(employeeLoginData);

      console.log("✅ Employee login re-authentication successful");
      toast.success("Successfully authenticated! Welcome back.");

      // Clear URL parameters
      clearUrlParams();

      return true;
    } else {
      console.error("Employee login failed:", employeeLoginData?.message);
      toast.error("Authentication failed. Please try logging in again.");
      clearUrlParams();
      return false;
    }
  } catch (error) {
    console.error("Employee login re-authentication error:", error);

    if (error.message.includes("Invalid credentials")) {
      toast.error("Invalid credentials. Please try logging in again.");
    } else {
      toast.error("Authentication failed. Please try logging in again.");
    }

    clearUrlParams();
    return false;
  }
};

/**
 * Clear authentication parameters from URL
 */
const clearUrlParams = () => {
  const url = new URL(window.location);
  url.searchParams.delete("loginType");
  url.searchParams.delete("firebaseToken");
  url.searchParams.delete("phoneNumber");
  url.searchParams.delete("credentials");

  // Update URL without page reload
  window.history.replaceState({}, document.title, url.toString());
  console.log("🧹 Cleared authentication parameters from URL");
};

/**
 * Check if current URL has authentication parameters
 */
export const hasUrlAuthentication = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("loginType");
};

/**
 * Get login type from URL parameters
 */
export const getUrlLoginType = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("loginType");
};
